// seedAfeDemo — seed a demo AFE program onto the existing demo spine (RM-14 / AFE S1–S5 demo scaffolding).
//
// The app's useDemoSeed seeds a demo pad + wells + drilling jobs, but no AFEs. This hangs an AFE PROGRAM
// off those wells so the Builder (/afes/:afeId/edit) and Viewer (/pads/:padId/afe) have something real to
// show: one facilities/pad AFE (the allocation source) + a primary_drilling AFE per well, each with an
// authored line-item budget. The demo has NO PadJob, so a facilities PadJob is minted as the pad-AFE
// anchor.
//
// Charter Abs-2: PURE demo scaffolding — the line amounts are AUTHORED demo constants, NOT computed. Every
// TOTAL / allocation / D&C split is still the Python engine's when the AFE is viewed (/api/afe/rollup).
// Re-seeds only a demo it wrote itself (DEMO_SEED_MARK + DEMO_SEED_VERSION); never touches a
// user-authored AFE.

import { useEffect } from "react";
import { useSyncExternalStore } from "react";
import { wellsStore, padJobsStore } from "../wells/store/spineStore";
import { AFES_STORAGE_KEY, AFE_LINKS_STORAGE_KEY } from "./hooks/useAfeData";
import { AFE_BUDGETS_STORAGE_KEY } from "./hooks/useAfeBudgetData";
import { generatePrefixedId } from "../../lib/id";
import { storage } from "../../lib/storage";
import { logger } from "../../lib/logger";
import { afeClassForJobType } from "./constants";
import { skeletonLines, DEFAULT_AFE_SECTIONS } from "./afeTaxonomy";
import type { Well } from "../wells/types";
import type { Afe, AfeBudget, AfeLine, AfeLink, PadJob } from "./types";

interface DemoRow {
  phase: string;
  cls: "TANGIBLE" | "INTANGIBLE";
  code: string;
  desc: string;
  /** Taxonomy leaf this line files under (afeTaxonomy). Without it the line lands in Ungrouped. */
  group?: string;
  amount: number;
  band?: string;
}

// A demo PRICE, addressed to a line the taxonomy skeleton already contains.
//
// WHY THIS SHAPE (PO 2026-08-24). The demo used to carry ~13 COARSE hand-authored lines
// ("Cementing services (all strings)", "Bits, downhole motors & MWD/directional"). Two things went
// wrong with that, and they were the same bug wearing two hats:
//
//   1. Those lines carried no `group`, so all of them rendered under "Ungrouped" — thirteen priced
//      rows filed under the bucket meant for orphans.
//   2. Far worse: a budget that HAS lines suppresses the skeleton entirely (useAfeBuilder prefers a
//      stored budget over seeding). So the demo AFE showed 13 lines INSTEAD OF the taxonomy's 180 —
//      every real group (Services, Equipment Rentals, Pressure Control…) rendered empty. The demo
//      AFE silently looked like the taxonomy was broken.
//
// The demo is now the SKELETON ITSELF with prices laid on top, so expanding any group shows the
// standard lines exactly as a fresh AFE does. A price whose address does not resolve to a real
// skeleton line is a hard failure, not a silently-dropped figure — see skeletonBudgetLines.
interface DemoPrice {
  /** Leaf id from afeTaxonomy — e.g. "rig_rate", "casing::sec-surface". */
  group: string;
  /** EXACT skeleton line description under that leaf. */
  description: string;
  amount: number;
  /** Accounting classification. Drives the engine's tangible/intangible split (afe_kernels
   *  rollup_lines reads cost_class); an unclassified line lands in `total` only, so dropping this
   *  made the Viewer's Tangible and Intangible panels read $0 against a ~$5M D&C. Only PRICED
   *  lines need it — an unpriced line contributes 0.0 and cannot move a split. */
  cls: "TANGIBLE" | "INTANGIBLE";
  /** Hole section this line belongs to, when the D&C dry-hole/completion split depends on it
   *  (afe_kernels default_dc_phase). Absent → the engine treats the line as dry-hole. */
  band?: string;
}

// Authored Bakken-typical demo figures. NOT computed, NOT a cost basis, NOT vendor pricing —
// round numbers chosen to make the roll-up and allocation demo legible (Abs-2: the TOTALS are
// still Python's). Deliberately sparse: a real AFE is mostly unpriced lines at draft stage.
const DEMO_PRICES: DemoPrice[] = [
  {
    group: "rig_rate",
    description: "Rig Day Rate — Operating",
    amount: 1480000,
    cls: "INTANGIBLE",
  },
  {
    group: "drilling",
    description: "Drill Pipe, HWDP & Collars",
    amount: 165000,
    cls: "INTANGIBLE",
  },
  {
    group: "pre_drilling",
    description: "Rig Mobilization",
    amount: 240000,
    cls: "INTANGIBLE",
  },
  {
    group: "pre_drilling",
    description: "Rig Demobilization",
    amount: 130000,
    cls: "INTANGIBLE",
  },
  {
    group: "solids_control",
    description: "Shakers",
    amount: 95000,
    cls: "INTANGIBLE",
  },
  {
    group: "evaluation",
    description: "Open-Hole Wireline Logging",
    amount: 310000,
    cls: "INTANGIBLE",
  },
  {
    group: "wellhead",
    description: "Wellhead",
    amount: 185000,
    cls: "TANGIBLE",
  },
  {
    group: "management",
    description: "Wellsite Supervisor",
    amount: 275000,
    cls: "INTANGIBLE",
  },
  {
    group: "contingency",
    description: "Contingency Allowance",
    amount: 310000,
    cls: "INTANGIBLE",
  },
  // Per-section — the whole point of the section program is that these are NOT one lump.
  {
    group: "casing::sec-surface",
    description: "Casing",
    amount: 245000,
    cls: "TANGIBLE",
    band: "surface",
  },
  {
    group: "casing::sec-intermediate",
    description: "Casing",
    amount: 430000,
    cls: "TANGIBLE",
    band: "intermediate",
  },
  {
    group: "casing::sec-production",
    description: "Casing",
    amount: 615000,
    cls: "TANGIBLE",
    band: "production",
  },
  {
    group: "cement::sec-surface",
    description: "Cement Service",
    amount: 95000,
    cls: "INTANGIBLE",
    band: "surface",
  },
  {
    group: "cement::sec-intermediate",
    description: "Cement Service",
    amount: 120000,
    cls: "INTANGIBLE",
    band: "intermediate",
  },
  {
    group: "cement::sec-production",
    description: "Cement Service",
    amount: 155000,
    cls: "INTANGIBLE",
    band: "production",
  },
  {
    group: "bha::sec-surface",
    description: "Bit",
    amount: 45000,
    cls: "INTANGIBLE",
    band: "surface",
  },
  {
    group: "bha::sec-intermediate",
    description: "Bit",
    amount: 85000,
    cls: "INTANGIBLE",
    band: "intermediate",
  },
  {
    group: "bha::sec-production",
    description: "Bit",
    amount: 130000,
    cls: "INTANGIBLE",
    band: "production",
  },
  {
    group: "bha::sec-production",
    description: "MWD Tool",
    amount: 295000,
    cls: "INTANGIBLE",
    band: "production",
  },
];

/** The taxonomy skeleton as demo AFE lines, with DEMO_PRICES laid onto the matching lines.
 *
 *  A price that matches NO skeleton line throws. Silently dropping it would let the demo drift out
 *  of sync with the taxonomy the moment a line is renamed in afeTaxonomy.json — which is exactly
 *  how the old coarse lines rotted into "Ungrouped" without anyone noticing. Loud beats lossy. */
function skeletonBudgetLines(
  afeId: string,
  now: string,
  mintId: (prefix: string) => string,
): AfeLine[] {
  const priced = new Map(
    DEMO_PRICES.map((d): [string, DemoPrice] => [
      `${d.group}\u0000${d.description}`,
      d,
    ]),
  );
  const hit = new Set<string>();

  const lines = skeletonLines(DEFAULT_AFE_SECTIONS).map((t, i): AfeLine => {
    const key = `${t.group}\u0000${t.description}`;
    const price = priced.get(key);
    if (price) hit.add(key);
    return {
      id: mintId("afeline"),
      afeId,
      sequenceNo: i + 1,
      group: t.group,
      description: t.description,
      ...(t.itemUnit ? { itemUnit: t.itemUnit } : {}),
      // BOTH `p50` and the top-level `amount` are written, deliberately. The Builder's band UI and
      // hero read p50; the engine's rollup_lines / split_dc read the TOP-LEVEL amount (see
      // afe_kernels.line_amount). They are separate paths, so a demo line priced only on p50 shows
      // a hero total while dcTotal and the tangible/intangible split read $0.
      //
      // KNOWN GAP, flagged not papered over: that split path means a line the USER prices in the
      // band grid (p50 only, no top-level amount) has the same problem. It is an engine/wire gap,
      // not a demo-seed one, and belongs in a separate fix — the demo must not paper over it by
      // pretending the shapes agree.
      ...(price
        ? {
            p50: { amount: { value: price.amount, currency: "USD" } },
            amount: { value: price.amount, currency: "USD" },
            costClass: price.cls,
            ...(price.band ? { requiresBand: price.band } : {}),
          }
        : {}),
      createdAt: now,
      modifiedAt: now,
    };
  });

  const orphans = [...priced.keys()].filter((k) => !hit.has(k));
  if (orphans.length > 0) {
    throw new Error(
      `seedAfeDemo: ${orphans.length} demo price(s) address no skeleton line — ` +
        `${orphans.map((k) => k.replace("\u0000", " → ")).join("; ")}. ` +
        `Fix DEMO_PRICES to match afeTaxonomy.json.`,
    );
  }
  return lines;
}

// Authored shared pad/civil lines (mirrors the prebake PAD_LINES) — sum = $1,165,000.
const DEMO_FACILITIES_LINES: DemoRow[] = [
  {
    phase: "CIVIL",
    cls: "INTANGIBLE",
    code: "100110",
    desc: "Location build & pad construction",
    group: "pre_drilling",
    amount: 420000,
  },
  {
    phase: "CIVIL",
    cls: "INTANGIBLE",
    code: "100111",
    desc: "Access road & site grading",
    group: "pre_drilling",
    amount: 160000,
  },
  {
    phase: "CIVIL",
    cls: "INTANGIBLE",
    code: "100112",
    desc: "Reserve pit / containment & liner",
    group: "pre_drilling",
    amount: 95000,
  },
  {
    phase: "FACILITY",
    cls: "TANGIBLE",
    code: "101110",
    desc: "Surface facilities & flowlines (shared)",
    group: "misc",
    amount: 280000,
  },
  {
    phase: "MOB",
    cls: "INTANGIBLE",
    code: "100120",
    desc: "Rig & camp mobilization to pad",
    group: "pre_drilling",
    amount: 150000,
  },
  {
    phase: "PERMIT",
    cls: "INTANGIBLE",
    code: "100130",
    desc: "Permitting, survey & regulatory",
    group: "pre_drilling",
    amount: 60000,
  },
];

export interface AfeDemoData {
  afes: Afe[];
  links: AfeLink[];
  budgets: AfeBudget[];
  /** Facilities PadJobs minted where a pad had none (the pad-AFE anchor). */
  newPadJobs: PadJob[];
}

/** PURE builder — the demo AFE program for the given spine. Testable (ids/time injected). */
export function buildAfeDemo(args: {
  wells: readonly Well[];
  padJobs: readonly PadJob[];
  now: string;
  mintId: (prefix: string) => string;
}): AfeDemoData {
  const { wells, padJobs, now, mintId } = args;
  const afes: Afe[] = [];
  const links: AfeLink[] = [];
  const budgets: AfeBudget[] = [];
  const newPadJobs: PadJob[] = [];

  const budgetOf = (
    afeId: string,
    scope: AfeBudget["scope"],
    rows: DemoRow[],
  ): AfeBudget => ({
    afeId,
    scope,
    lines: rows.map(
      (r, i): AfeLine => ({
        id: mintId("afeline"),
        afeId,
        sequenceNo: i + 1,
        activityPhase: r.phase,
        costClass: r.cls,
        costCode: r.code,
        ...(r.group ? { group: r.group } : {}),
        description: r.desc,
        itemUnit: "ls",
        amount: { value: r.amount, currency: "USD" },
        ...(r.band ? { requiresBand: r.band } : {}),
        createdAt: now,
        modifiedAt: now,
      }),
    ),
  });

  // ── Per-well drilling AFEs ──
  const padsWithWellAfe = new Set<string>();
  for (const w of wells) {
    if (w.archivedAt || !w.fieldPadId) continue;
    let jobId: string | undefined;
    for (const wb of w.wellbores ?? []) {
      for (const j of wb.jobs ?? []) {
        if (
          !j.archivedAt &&
          afeClassForJobType(j.jobType) === "primary_drilling"
        ) {
          jobId = j.id;
          break;
        }
      }
      if (jobId) break;
    }
    if (!jobId) continue;

    const afeId = mintId("afe");
    afes.push({
      id: afeId,
      name: `${w.wellName} drilling`,
      afeNumber: `AFE-${w.wellName}`,
      afeType: "original",
      afeClass: "primary_drilling",
      seededBy: DEMO_SEED_MARK,
      currency: "USD",
      status: "draft",
      completionMethod: "plug_and_perf",
      createdAt: now,
      modifiedAt: now,
    });
    budgets.push({
      afeId,
      scope: "well",
      lines: skeletonBudgetLines(afeId, now, mintId),
    });
    links.push({ id: mintId("afelink"), afeId, jobId, jobKind: "well" });
    padsWithWellAfe.add(w.fieldPadId);
  }

  // ── Facilities/pad AFE per pad (the allocation source) — mint a PadJob if the pad has none ──
  for (const padId of padsWithWellAfe) {
    let padJob = padJobs.find((pj) => pj.padId === padId && !pj.archivedAt);
    if (!padJob) {
      padJob = {
        id: mintId("padjob"),
        padId,
        name: "Pad Facilities & Civil",
        jobType: "facilities",
        status: "Planned",
        createdAt: now,
        modifiedAt: now,
      };
      newPadJobs.push(padJob);
    }
    const afeId = mintId("afe");
    afes.push({
      id: afeId,
      name: `Pad facilities ${padId.slice(-4).toUpperCase()}`,
      afeNumber: `AFE-PAD-${padId.slice(-4).toUpperCase()}`,
      afeType: "original",
      afeClass: "facilities",
      seededBy: DEMO_SEED_MARK,
      currency: "USD",
      status: "draft",
      allocationBasis: "equal",
      createdAt: now,
      modifiedAt: now,
    });
    budgets.push(budgetOf(afeId, "pad", DEMO_FACILITIES_LINES));
    links.push({
      id: mintId("afelink"),
      afeId,
      jobId: padJob.id,
      jobKind: "pad",
    });
  }

  return { afes, links, budgets, newPadJobs };
}

/** Write one seed store. RETURNS whether it landed (AFE-15 F1).
 *
 *  This used to swallow. On a near-full origin the budgets write — much the largest of the three —
 *  was dropped silently while the few-byte version marker below succeeded, so the seed stamped
 *  itself COMPLETE with headers and links present and no budgets. Every later boot then returned
 *  early at the version guard, making the damage permanent and self-concealing.
 *
 *  The plan says to MIRROR `lib/storage.ts` `setItem` and not to invent a third convention. This
 *  delegates to it instead, which reaches the same place with less code: that helper already
 *  returns a boolean and already logs the key and the byte count that did not save. */
function writeJSON(key: string, value: unknown): boolean {
  return storage.setItem(key, JSON.stringify(value));
}

/** Clear the demo AFE stores so the seed can re-fire against a fresh spine. MUST be called by any
 *  full demo-spine reset (reloadDemoData) — otherwise the stale AfeLinks point at deleted job ids and
 *  the seed's idempotency guard blocks re-seeding, so the AFE program resolves empty (review S5-demo-1).
 *
 *  Also clears the seed-version key, without which a reset store would immediately be judged
 *  "up to date" and never re-seed. DESTRUCTIVE — seedAfeDemo calls this only once it holds a
 *  complete replacement. */
export function resetAfeDemo(): void {
  try {
    for (const k of [
      AFES_STORAGE_KEY,
      AFE_LINKS_STORAGE_KEY,
      AFE_BUDGETS_STORAGE_KEY,
      DEMO_SEED_VERSION_KEY,
    ]) {
      localStorage.removeItem(k);
    }
  } catch {
    /* storage unavailable — best-effort demo path */
  }
}

// Bump when the SHAPE of the seeded demo changes, so an existing browser re-seeds instead of
// sitting on stale data forever — a demo fixed in code otherwise stays broken on every machine that
// already ran the old one, which is how the coarse Ungrouped lines survived their first fix.
//
// ⚠ READ/WRITE SYMMETRY. The version is stored with a RAW setItem and read with a RAW getItem.
// It is deliberately NOT routed through writeJSON: that JSON.stringifies, so a string round-trips
// as '"2026-08-24-skeleton"' — quotes included — and the comparison below could never be true.
// The seed then judged itself stale on EVERY load and wiped the store each time (adversarial review
// 2026-08-24, Sev-1). Any future key compared as a raw string must be written the same way.
const DEMO_SEED_VERSION = "2026-08-24-skeleton";
const DEMO_SEED_VERSION_KEY = "oxbo-afe-demo-seed-version";

/** Stamped on every AFE this seed writes. The ONLY thing that makes an AFE safe to replace. */
const DEMO_SEED_MARK = "afe-demo-seed";

/** True when every stored AFE was written by this seed, so replacing them destroys nothing of the
 *  user's (Abs-7).
 *
 *  Identity is an EXPLICIT MARK, never an inferred one. Classifying by `afeNumber` prefix + class
 *  was a latent data-loss bug: `types.ts` documents that an AFE gets its number from accounting
 *  LATER, so the moment the Builder grows an "AFE Number" field, an engineer typing the ordinary
 *  `AFE-2026-0142` on a primary_drilling AFE would have matched — and the next load would have
 *  deleted their entire AFE register. A heuristic that is one UI field away from eating real work
 *  is not a guard. Anything without the mark is treated as the user's, including AFEs seeded by
 *  versions predating it (those are simply never auto-replaced — the Settings reset still clears
 *  them by hand). */
function onlySeededAfesExist(stored: Afe[]): boolean {
  return (
    stored.length > 0 && stored.every((a) => a.seededBy === DEMO_SEED_MARK)
  );
}

/** Seed the demo AFE program.
 *
 *  No-op when the store already holds an up-to-date demo, or ANY AFE this seed did not write, or
 *  when the spine has no drillable wells. */
export function seedAfeDemo(): void {
  try {
    const existing = localStorage.getItem(AFES_STORAGE_KEY);
    const stored = existing ? (JSON.parse(existing) as Afe[]) : [];
    if (stored.length > 0) {
      const seededVersion = localStorage.getItem(DEMO_SEED_VERSION_KEY);
      // Up to date, or the store holds AFEs a human authored → leave it completely alone.
      if (seededVersion === DEMO_SEED_VERSION || !onlySeededAfesExist(stored))
        return;
      // Past this point the store holds a STALE seed of ours, which the writes below replace in
      // place. It used to be tracked in a `stale` flag whose only consumer was the reset that
      // AFE-15 F1 removed; the flag went with it.
    }

    // BUILD FIRST, DESTROY LAST. The reset used to run here, before the build — so every path that
    // bailed afterwards (no drillable well, or skeletonBudgetLines throwing on a renamed taxonomy
    // line) left localStorage permanently EMPTY, with the catch below swallowing the error. A
    // rename in afeTaxonomy.json could wipe the AFE register and keep it wiped (review Sev-1).
    // Nothing is removed until a complete replacement is in hand.
    const wells = wellsStore.read();
    const padJobs = padJobsStore.read();
    const { afes, links, budgets, newPadJobs } = buildAfeDemo({
      wells,
      padJobs,
      now: new Date().toISOString(),
      mintId: generatePrefixedId,
    });
    if (afes.length === 0) return; // no drillable demo wells yet — leave whatever is stored

    // NO RESET HERE (AFE-15 F1). `if (stale) resetAfeDemo()` used to run at this point, removing
    // all three data keys BEFORE three writes that can each fail — so on the stale path a failed
    // write left nothing at all, destroying the prior seed to make room for a replacement that
    // never arrived.
    //
    // Verified redundant rather than assumed so (the plan asks for exactly this check): all three
    // writes below are WHOLE-ARRAY replacements, so removing the same three keys first changes
    // nothing they do; and the reset's only other effect is clearing the version key, which is
    // either re-stamped below on full success or deliberately left stale on failure so the next
    // boot retries. (The plan says "which line 542 immediately re-stamps" — true of the code as it
    // was, unconditional and five lines on. After this change the re-stamp is conditional, which
    // strengthens the conclusion rather than weakening it.) What removal costs is the destructive
    // window and nothing else. `resetAfeDemo` stays exported and unchanged for `reloadDemoData`,
    // which does need it.
    const wrote = [
      writeJSON(AFES_STORAGE_KEY, afes),
      writeJSON(AFE_LINKS_STORAGE_KEY, links),
      writeJSON(AFE_BUDGETS_STORAGE_KEY, budgets),
    ];
    if (!wrote.every(Boolean)) {
      // A PARTIAL SEED MUST NOT MARK ITSELF DONE. Leaving the version key unset is what lets the
      // next boot try again; stamping it here is what made the old failure permanent. A swallowed
      // demo seed is acceptable, a silent one is not — `storage.setItem` has already logged which
      // key and how many bytes, and this names the consequence.
      logger.error(
        "AFE demo seed did not complete — the version marker was NOT stamped, so the next boot will retry.",
        {
          afes: wrote[0],
          links: wrote[1],
          budgets: wrote[2],
        },
      );
      return;
    }
    // Raw — see the read/write symmetry note on DEMO_SEED_VERSION. Reached only when all three
    // data writes landed, so the marker means what it says.
    localStorage.setItem(DEMO_SEED_VERSION_KEY, DEMO_SEED_VERSION);
    if (newPadJobs.length > 0) {
      padJobsStore.setAll([...padJobs, ...newPadJobs]); // reactive — the pad dashboard picks it up
    }
  } catch (err) {
    // Demo scaffolding must never crash the app (mirrors useDemoSeed).
    console.error("seedAfeDemo: failed to seed demo AFEs", err);
  }
}

/** Fire the AFE demo seed once, after the spine seed has produced wells (idempotent). */
export function useAfeDemoSeed(): void {
  const wells = useSyncExternalStore(
    wellsStore.subscribe,
    wellsStore.read,
    wellsStore.read,
  );
  useEffect(() => {
    if (wells.length === 0) return;
    seedAfeDemo();
  }, [wells.length]);
}

export default useAfeDemoSeed;
